import { CountryFlagLabel } from "@/components/Common/CountryFlagLabel";
import { SimpleTooltip } from "@/components/Common/Tooltip/SimpleTooltip";
import { RequestManagedCluster } from "@/components/DevicePage/RequestManagedCluster/RequestManagedCluster";
import { TickIcon } from "@/components/Icons/TickIcon";
import { Button } from "@/components/ui/button";
import { INVENTORY_REGIONS } from "@/constants";
import { REGION_BY_ISO2 } from "@/constants/map";
import { cn } from "@/lib/utils";
import { ClusterType, DynamicSupplyHardware, RequestManagedClusterFormType } from "@/types";
import { getClusterTypeDisplayName, submitHardwareRequestToHubSpot } from "@/utils/cluster";
import { HARDWARE_MAP } from "@/utils/mapping";
import { formatCapacityFromGb } from "@/utils/number";
import React, { useState } from "react";
import { VerticalFlowAnalytics } from "./analytics";
import { ConfidentialComputeBadge } from "./components/ConfidentialComputeBadge";
import { useHardwareDiscount } from "./hooks/useHardwareDiscount";
import { HardwareDiscountTag } from "./components/HardwareDiscountTag";
import { useHardwareTags } from "./hooks/useHardwareTags";

export const REQUESTED_DEVICES_KEY = "requested_devices";

export const addToRequestedDevices = (hardwareName: string, deploymentId: string) => {
  const requestedDevices: Record<string, string> = JSON.parse(
    localStorage.getItem(REQUESTED_DEVICES_KEY) || "{}"
  );
  requestedDevices[hardwareName] = deploymentId;
  localStorage.setItem(REQUESTED_DEVICES_KEY, JSON.stringify(requestedDevices));
};

export const getRequestedDevices = () => {
  const requestedDevices: Record<string, string> = JSON.parse(
    localStorage.getItem(REQUESTED_DEVICES_KEY) || "{}"
  );
  return requestedDevices;
};

export const isDeviceRequested = (hardwareName: string, deploymentId: string) => {
  const requestedDevices = getRequestedDevices();
  return requestedDevices[hardwareName] === deploymentId;
};

type HardwareCardProps = {
  item: DynamicSupplyHardware;
  isSelected: boolean;
  onSelect: (id: string) => void;
  onAdvanceProcessorStep?: (hardware: DynamicSupplyHardware) => void;
  onShowDetails: (item: DynamicSupplyHardware) => void;
  analytics?: Pick<
    VerticalFlowAnalytics,
    "trackRequestDevicesClicked" | "trackRequestDevicesSubmitted"
  >;
  description: string;
  hiddenHardwareFields: string[];
  clusterType?: ClusterType; // Optional cluster type for HubSpot submission
};

export const HardwareCard: React.FC<HardwareCardProps> = ({
  item,
  isSelected,
  onSelect,
  onAdvanceProcessorStep,
  onShowDetails,
  analytics,
  description,
  hiddenHardwareFields = [],
  clusterType
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const manufacturer = HARDWARE_MAP["nvidia"];

  const disabled = !!item.isSoldOut;
  const isRequested = isDeviceRequested(item.hardwareName, item.deploymentId as string);
  const formattedStorage = formatCapacityFromGb(item.storage, { fallback: "N/A" });

  const isConfidentialCompute = item.isConfidentialCompute && !item.isSoldOut;
  const { originalPrice, discount, hasDiscount, isPriceComparisonEnabled } =
    useHardwareDiscount(item);

  const { cardClassName, tagLabel, tagClassName, tagColor } = useHardwareTags(item.hardwareName);

  return (
    <>
      <div
        className={cn(
          "relative flex flex-col rounded-[8px] border border-gray-dark-200 bg-highlight transition-colors md:max-h-[347px]",
          isSelected ? "border-black dark:border-white" : "",
          disabled ? "cursor-default" : "cursor-pointer hover:border-black dark:hover:border-white",
          cardClassName
        )}
        id={`hardware-card-${item.hardwareId}`}
        onClick={() => {
          if (disabled) return;
          onSelect(item.hardwareId);
        }}
        onDoubleClick={() => {
          if (disabled || !onAdvanceProcessorStep) return;
          onSelect(item.hardwareId);
          onAdvanceProcessorStep(item);
        }}
      >
        {/* Tag */}
        {tagLabel && (
          <div
            className={cn(
              "absolute left-5 top-0 flex translate-y-[-50%] items-center justify-center gap-1.5 rounded-full px-2 py-[2px] text-[10px] font-medium leading-[130%]",
              tagClassName
            )}
          >
            <div className={cn("h-1.5 w-1.5 rounded-full", tagColor)} />
            <span>{tagLabel}</span>
          </div>
        )}
        {/* Header */}
        <div className="flex flex-col gap-3 p-4">
          <div className="flex min-w-0 items-center gap-3">
            {manufacturer?.hardwareManufacturerIcon ? (
              <manufacturer.hardwareManufacturerIcon
                className={cn("h-[24px] w-[24px] shrink-0", manufacturer.hardwareManufacturerColor)}
              />
            ) : null}
            <SimpleTooltip
              id={`hardware-name-tooltip-${item.hardwareId}`}
              content={item.hardwareName}
              usePortal
              className="label-medium min-w-0 truncate font-medium leading-6 text-foreground"
            >
              {item.hardwareName}
            </SimpleTooltip>
            <div className="label-small shrink-0 rounded-[4px] border border-border px-2 py-1 font-medium leading-4 text-foreground dark:bg-gray-light-1800">{`x${item.numCards}`}</div>
            <div className="flex-1" />
            {item.isSoldOut ? (
              <>
                <div className="label-x-small flex-shrink-0 rounded-[4px] bg-container px-2 py-[6px] font-medium leading-[130%] text-[#EF4444] dark:bg-[#301414]">
                  Sold Out
                </div>
              </>
            ) : (
              <div
                className={cn(
                  "radio flex h-[24px] w-[24px] flex-shrink-0 items-center justify-center rounded-full border border-gray-dark-200",
                  disabled ? "bg-gray-light-1900" : "bg-gray-dark-600"
                )}
              >
                {isSelected && (
                  <div
                    key="selected-dot"
                    className="h-[10px] w-[10px] rounded-full bg-black/75 dark:bg-gray-light-1000"
                  />
                )}
              </div>
            )}
          </div>
          {isConfidentialCompute && <ConfidentialComputeBadge size="small" />}
        </div>

        {/* Details */}
        <div className="mt-auto flex flex-col gap-3 border-t border-gray-dark-200 p-4">
          {/* <KeyValue label="Price" value={`$${item.price.toFixed(2)}/hr`} /> */}
          <div className="flex flex-row items-center justify-between">
            <div className="flex items-baseline gap-2">
              <div className="label-small leading-5 text-gray-dark-2200">Price</div>
              <HardwareDiscountTag
                hasDiscount={hasDiscount}
                discount={discount}
                isPriceComparisonEnabled={isPriceComparisonEnabled}
                hardwareId={item.hardwareId}
              />
            </div>
            <div className="flex items-center gap-2">
              {hasDiscount && originalPrice ? (
                <div className="label-small text-gray-dark-2200 line-through">
                  ${originalPrice.toFixed(2)}/hr
                </div>
              ) : null}
              <div className="label-medium text-foreground">${item.price.toFixed(2)}/hr</div>
            </div>
          </div>
          <KeyValue label="VRAM Per Card" value={`${item.vramPerCard} GB`} />
          {!hiddenHardwareFields.includes("storage") && (
            <KeyValue label="Storage" value={formattedStorage} />
          )}
          {!hiddenHardwareFields.includes("supplier") && (
            <KeyValue label="Supplier" value={item.supplier === "external" ? "Partner" : "IO"} />
          )}
          <div className="flex items-center justify-between">
            <div className="label-small leading-5 text-gray-dark-2200">Location</div>
            <CountryFlagLabel code={item.location} name={item.location} />
          </div>
          <div className={cn("mt-2 flex flex-row-reverse items-center gap-2")}>
            <Button
              variant="outline"
              size="xs"
              className={"md:w-full"}
              onClick={(e) => {
                e.stopPropagation();
                onShowDetails(item);
              }}
              onDoubleClick={(e) => e.stopPropagation()}
            >
              More details
            </Button>
            {item.isSoldOut && (
              <Button
                variant="outline"
                size="xs"
                className={"md:w-full"}
                onClick={(e) => {
                  e.stopPropagation();
                  if (isRequested) return;
                  setIsModalOpen(true);
                  analytics?.trackRequestDevicesClicked({ name: item.hardwareName });
                }}
                onDoubleClick={(e) => e.stopPropagation()}
              >
                {isRequested ? <TickIcon className="mr-1 h-4 w-4" /> : null}
                {isRequested ? "Requested" : "Request"}
              </Button>
            )}
          </div>
        </div>
      </div>
      {isModalOpen && (
        <RequestManagedCluster
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          selectedFilters={{
            hardwareNames: [item.hardwareName],
            connectivityTiers: [4],
            hardwareQuantity: [item.numCards || 1],
            regions: REGION_BY_ISO2[item.location]
              ? [REGION_BY_ISO2[item.location]]
              : INVENTORY_REGIONS,
            confidentialCompute: [!!item.isConfidentialCompute]
          }}
          additionalFilters={{ hardware_names: [item.hardwareName] }}
          title={`Device Request`}
          description={description}
          hideConnectivityTier={true}
          onRequestSubmitted={() => {
            addToRequestedDevices(item.hardwareName, item.deploymentId as string);
            analytics?.trackRequestDevicesSubmitted({ name: item.hardwareName });
          }}
          onSubmitOverride={async (values: RequestManagedClusterFormType) => {
            // Format regions - ensure they match HubSpot expected values
            const formattedRegions = values.regions.map((region) => {
              // Map any variations to standard values
              if (region === "LATAM America") return "LATAM";
              return region;
            });

            // Get hardware name (should be single value from the card)
            const hardwareName =
              values.hardwareNames.length > 0 ? values.hardwareNames[0] : item.hardwareName;

            // Format date if provided
            const requiredBy = values.neededBy
              ? values.neededBy.toISOString().split("T")[0]
              : undefined;

            return await submitHardwareRequestToHubSpot({
              regions: formattedRegions,
              hardwareName,
              confidentialCompute: values.confidentialCompute || false,
              numberOfGpus: values.gpuQuantity || 1,
              contactEmail: values.contactEmail,
              clusterType: clusterType ? getClusterTypeDisplayName(clusterType) : "Unknown", // Hidden field to identify cluster type
              telegramHandle: values.telegramHandle,
              requiredBy
            });
          }}
        />
      )}
    </>
  );
};

const KeyValue: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => {
  return (
    <div className="flex items-center justify-between">
      <div className="label-small leading-5 text-gray-dark-2200">{label}</div>
      <div className="subtext-medium font-medium leading-6 text-foreground">{value}</div>
    </div>
  );
};
